import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { ActiveBadge } from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";

export default function CustomersPage() {
  const { isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");

  const load = async () => {
    const res = await api.get("/customers");
    setCustomers(res.data);
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
  }, []);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setName(c.name);
    setDialogOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/customers/${editing.id}`, { name });
        toast.success("Customer updated");
      } else {
        await api.post("/customers", { name });
        toast.success("Customer created");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/customers/${c.id}`, { active: !c.active });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="customers-page">
      <PageHeader
        title="Customers"
        subtitle="Lightweight customer master data (not a CRM)"
        actions={
          isAdmin && (
            <Button onClick={openCreate} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="new-customer-button">
              <Plus className="h-4 w-4 mr-1.5" /> New customer
            </Button>
          )
        }
      />
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="customers-table">
        <table className="w-full">
          <thead>
            <tr className="bg-secondary/50 border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Customer</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Status</th>
              <th className="py-3 px-4" />
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-accent/40 transition-colors" data-testid={`customer-row-${c.id}`}>
                <td className="py-3 px-4 text-sm font-semibold text-slate-800">{c.name}</td>
                <td className="py-3 px-4"><ActiveBadge active={c.active} testId={`customer-status-${c.id}`} /></td>
                <td className="py-3 px-4 text-right">
                  {isAdmin && (
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}>Edit</Button>
                      <Button variant="outline" size="sm" onClick={() => toggleActive(c)} data-testid={`toggle-customer-${c.id}`}>
                        {c.active ? "Deactivate" : "Activate"}
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {customers.length === 0 && <EmptyState title="No customers yet" testId="customers-empty" />}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit customer" : "New customer"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 py-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="customer-name-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="customer-cancel-button">Cancel</Button>
            <Button onClick={save} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="customer-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
